import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { PageHeader } from '@/components/ui/PageHeader'
import { ApiError } from '@/api/errors'
import { studentQuery, useUpdateStudent } from '@/features/students/api'
import { StudentForm } from '@/features/students/components/StudentForm'
import { toFormValues, toPayload } from '@/features/students/mappers'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function StudentEditPage() {
  const { t } = useTranslation('students')
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const { data, isPending, error, refetch } = useQuery(studentQuery(id))
  const update = useUpdateStudent()
  useDocumentTitle(data ? t('Edit {{name}}', { name: data.summary.fullName }) : 'Edit student')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="student" homeHref="/admin/students" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  return (
    <>
      <PageHeader
        title={t('Edit {{name}}', { name: data.summary.fullName })}
        description={`${data.summary.studentCode} · ${t('placement changes are made from the student page (promote / change section).')}`}
        breadcrumbs={[
          { label: t('Students'), to: '/admin/students' },
          { label: data.summary.fullName, to: `/admin/students/${id}` },
          { label: t('Edit') },
        ]}
        homeHref="/admin"
      />
      <StudentForm
        mode="edit"
        submitLabel={t('Save changes')}
        photoUrl={data.summary.photoUrl}
        defaultValues={toFormValues(data)}
        onCancel={() => void navigate(`/admin/students/${id}`)}
        onSubmit={async (values) => {
          await update.mutateAsync({ id, ...toPayload(values) })
          toast.success(t('Changes saved'))
          void navigate(`/admin/students/${id}`)
        }}
      />
    </>
  )
}
